import { useEffect } from 'react';
import Footer from '../../home/components/Footer';

export default function CustomCarpentryPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Custom Carpentry',
      description: 'Expert custom carpentry in Southeast Michigan — built-in shelving, crown molding, wainscoting, custom doors, window trim, and staircase work by American Dream Remodeling INC.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'American Dream Remodeling INC',
        url: siteUrl,
        telephone: '+1-586-443-0294',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Detroit',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      url: `${siteUrl}/services/custom-carpentry`,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
        { '@type': 'ListItem', position: 3, name: 'Custom Carpentry', item: `${siteUrl}/services/custom-carpentry` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What types of custom carpentry do you offer in Southeast Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'American Dream Remodeling INC offers built-in shelving, crown molding, wainscoting, custom doors, window trim, and staircase work throughout Macomb, Oakland, Wayne, and Washtenaw County.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does custom carpentry installation take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Custom carpentry timelines vary by project scope. Simple trim work may take 1–3 days, while full built-in installations can take 1–2 weeks. We provide a detailed timeline during your free consultation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can you match existing woodwork in my home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, our skilled carpenters carefully match existing profiles, wood species, and finishes to ensure new carpentry blends seamlessly with your home\'s existing woodwork.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you offer free estimates for custom carpentry projects?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC provides free, no-obligation estimates for all custom carpentry projects throughout Southeast Michigan.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Custom Carpentry in Southeast Michigan | American Dream Remodeling INC',
      description: 'Expert custom carpentry in Southeast Michigan — built-in shelving, crown molding, wainscoting, custom doors, window trim, and staircase work by American Dream Remodeling INC.',
      url: `${siteUrl}/services/custom-carpentry`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: 'Custom Carpentry', item: `${siteUrl}/services/custom-carpentry` },
        ],
      },
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-carpentry-service', serviceSchema);
    injectSchema('schema-carpentry-breadcrumb', breadcrumb);
    injectSchema('schema-carpentry-faq', faqSchema);
    injectSchema('schema-carpentry-webpage', webPageSchema);

    return () => {
      ['schema-carpentry-service', 'schema-carpentry-breadcrumb', 'schema-carpentry-faq', 'schema-carpentry-webpage'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToEstimate = () => {
    sessionStorage.setItem('scrollToEstimate', 'true');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b8f430b0c4138c3ccaf908ac4faeb59e.jpg"
            alt="Custom Carpentry Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Custom Carpentry</h1>
          <p className="text-xl md:text-2xl mb-8">Expert craftsmanship for built-in shelving, crown molding, wainscoting, and custom woodwork that adds character to your home.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Craftsmanship That Stands Out</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Custom carpentry adds unique character and functionality to your home. From elegant crown molding to built-in storage solutions, quality woodwork transforms ordinary spaces into extraordinary ones.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At American Dream Remodeling INC, our skilled carpenters bring decades of experience to every project. We take pride in precision craftsmanship and attention to detail, creating custom pieces that enhance your home's beauty and value.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20crown%20molding%20and%20wainscoting%20detail%20white%20trim%20professional%20carpentry%20work%20clean%20simple%20background%20professional%20interior%20photography&width=800&height=600&seq=carpentry-detail-001&orientation=landscape"
                alt="Crown Molding Detail"
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-bookshelf-line', title: 'Built-In Shelving', desc: 'Custom storage and display solutions' },
                { icon: 'ri-layout-top-line', title: 'Crown Molding', desc: 'Elegant ceiling trim and details' },
                { icon: 'ri-layout-bottom-line', title: 'Wainscoting', desc: 'Classic wall paneling and trim' },
                { icon: 'ri-door-line', title: 'Custom Doors', desc: 'Interior and exterior door installation' },
                { icon: 'ri-window-line', title: 'Window Trim', desc: 'Beautiful casing and sills' },
                { icon: 'ri-stairs-line', title: 'Staircase Work', desc: 'Railings, balusters, and treads' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Consultation', desc: 'Discuss your vision and requirements' },
                { step: '2', title: 'Design', desc: 'Create detailed plans and measurements' },
                { step: '3', title: 'Crafting', desc: 'Precision cutting and assembly' },
                { step: '4', title: 'Installation', desc: 'Expert fitting and finishing' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready for Custom Carpentry?</h2>
          <p className="text-xl text-white/90 mb-8">Get a free, no-obligation estimate for your custom carpentry project in Southeast Michigan.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Your Free Estimate
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}